import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import { loginWithEmail } from "../services/authService";

const LoginPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Si ya está autenticado, redirigir al dashboard
  if (isAuthenticated) {
    navigate("/", { replace: true });
    return null;
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setError("Por favor, completa todos los campos");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await loginWithEmail(formData.email, formData.password);
      navigate("/", { replace: true });
    } catch (err) {
      console.error("Error de login:", err.code);
      const errorMessages = {
        "auth/invalid-credential": "Email o contraseña incorrectos",
        "auth/user-not-found": "No existe una cuenta con este email",
        "auth/wrong-password": "Contraseña incorrecta",
        "auth/invalid-email": "Email no válido",
        "auth/user-disabled": "Cuenta deshabilitada",
        "auth/too-many-requests": "Demasiados intentos. Intenta más tarde",
        "auth/invalid-login-credentials": "Email o contraseña incorrectos",
      };
      setError(
        errorMessages[err.code] ||
          "Error al iniciar sesión. Verifica tus credenciales.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, #003d42 0%, #005f65 50%, #00afa3 100%)",
        p: 2,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 20% 50%, rgba(0,175,163,0.1) 0%, transparent 50%)",
          pointerEvents: "none",
        },
      }}
    >
      <Card
        sx={{
          maxWidth: 440,
          width: "100%",
          p: 4,
          borderRadius: 4,
          boxShadow: "0 8px 32px rgba(0, 95, 101, 0.3)",
        }}
      >
        {/* Logo */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 3,
              backgroundColor: "#000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.35)",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src="/epcorelogo.png"
              alt="EPC Logo"
              sx={{
                width: "90%",
                height: "90%",
                objectFit: "contain",
              }}
            />
          </Box>

          <Typography
            variant="h5"
            fontWeight={700}
            gutterBottom
            sx={{
              background: "linear-gradient(135deg, #005f65, #00afa3)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            EPCore
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Acceso exclusivo para personal autorizado
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            autoFocus
            sx={{
              mb: 2.5,
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "#00afa3",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#005f65",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#005f65",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email fontSize="small" sx={{ color: "#00afa3" }} />
                </InputAdornment>
              ),
            }}
            placeholder="admin@epcsolar.es"
          />

          <TextField
            fullWidth
            label="Contraseña"
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleChange}
            required
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "#00afa3",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#005f65",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#005f65",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock fontSize="small" sx={{ color: "#00afa3" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            placeholder="••••••••"
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              py: 1.5,
              fontWeight: 600,
              fontSize: "1rem",
              textTransform: "none",
              background: "linear-gradient(135deg, #005f65 0%, #00afa3 100%)",
              boxShadow: "0 4px 12px rgba(0, 95, 101, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #004d52 0%, #009688 100%)",
                boxShadow: "0 6px 16px rgba(0, 95, 101, 0.4)",
              },
              "&:disabled": {
                background: "rgba(0, 95, 101, 0.3)",
              },
            }}
          >
            {loading ? "Verificando credenciales..." : "Iniciar Sesión"}
          </Button>
        </form>

        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Solo 4 usuarios autorizados
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Contacta al administrador si necesitas acceso
          </Typography>
        </Box>
      </Card>
    </Box>
  );
};

export default LoginPage;
